import React, { useEffect, useState } from "react";
import { Outlet, useParams, useNavigate } from "react-router-dom";
import { Workflow, Agent, Event } from "../../shared/types";
import { workflowApi } from "../services/api";
import { WorkflowProvider } from "../contexts/WorkflowContext";

export const WorkflowLayout: React.FC = () => {
  const { workflowId } = useParams<{ workflowId: string }>();
  const navigate = useNavigate();
  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load workflow data when workflowId changes
  useEffect(() => {
    if (!workflowId) {
      setError("No workflow ID provided");
      setLoading(false);
      return;
    }

    const loadWorkflow = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await workflowApi.get(workflowId);
        if (result.success && result.data) {
          const data = result.data as any;
          const wf = data.workflow || data;
          const workflowAgents = data.agents || [];
          const workflowEvents = data.events || [];
          
          setWorkflow(wf);
          setAgents(workflowAgents);
          setEvents(workflowEvents);
          
          // If no events in the response, try fetching them separately
          if (workflowEvents.length === 0) {
            try {
              const eventsRes = await workflowApi.getEvents(workflowId);
              if (eventsRes?.success && eventsRes.data) {
                const eventsData = eventsRes.data as { events: Event[] } | Event[];
                const eventsList = Array.isArray(eventsData) ? eventsData : (eventsData.events || []);
                setEvents(eventsList);
              }
            } catch (e) {
              console.warn("Failed to fetch additional events:", e);
            }
          }
        } else {
          setError("Workflow not found");
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load workflow");
      } finally {
        setLoading(false);
      }
    };

    loadWorkflow();
  }, [workflowId]);

  // Redirect to workflows list if workflow not found
  useEffect(() => {
    if (!loading && error && error === "Workflow not found") {
      navigate('/app', { replace: true });
    }
  }, [loading, error, navigate]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-300">Loading workflow...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-4">Error</div>
          <p className="text-gray-300 mb-4">{error}</p>
          <button
            onClick={() => navigate('/app')}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded"
          >
            Back to Workflows
          </button>
        </div>
      </div>
    );
  }

  if (!workflow) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-500 text-lg mb-4">No workflow data</div>
          <button
            onClick={() => navigate('/app')}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded"
          >
            Back to Workflows
          </button>
        </div>
      </div>
    );
  }

  return (
    <WorkflowProvider
      workflow={workflow}
      agents={agents}
      events={events}
      isConnected={isConnected}
      isRunning={isRunning}
      onWorkflowUpdate={setWorkflow}
      onAgentsUpdate={setAgents}
      onEventsUpdate={setEvents}
      onConnectionUpdate={setIsConnected}
      onRunningUpdate={setIsRunning}
    >
      <Outlet />
    </WorkflowProvider>
  );
};
